import type { TransferStatusName } from "../design-system/format/status.ts";
import type { Tenant } from "./tenants.ts";

/**
 * The reference flow's transfer state — one owner, for the whole flow.
 *
 * ## Why this module exists
 *
 * It was previously two states: the compose and review screens read a
 * client-local draft, while the status screen read a fixed fixture. They
 * disagreed, so an amount typed on the first screen was replaced by the
 * fixture's amount on the last one — three screens about the same transfer,
 * showing two different values. That is a correctness bug in the product's most
 * basic promise, not a cosmetic one, so the two states are collapsed into one
 * here and the routes are given no way to reach a second.
 *
 * ## The two states, and the line between them
 *
 * PHASE_01_PRODUCTION_ARCHITECTURE.md §12 puts `DRAFT_AMOUNT` outside the
 * server graph — "[client-local] DRAFT_AMOUNT (never a server state)" — and
 * makes every status after it bank-authoritative. This module keeps that shape:
 *
 * - `draft` is the client-local amount being typed. It is memory-only and gone
 *   on reload, per `state.ts`.
 * - `active` is the reference transfer the draft became. In production it would
 *   be a bank-issued record read back from the bank's projection; here it is
 *   the same values carried forward, so the flow is coherent without pretending
 *   a bank was involved.
 *
 * ## What this is deliberately not
 *
 * Not persistence: nothing is written to `localStorage`, `sessionStorage`,
 * `IndexedDB`, a cookie, or a URL. Not a server: nothing is fetched and no
 * transfer is executed. Not an identifier mint: the opaque reference is a fixed
 * synthetic literal, because `docs/roadmap/PHASE_03_EARLY_FOUNDATION_DECISION.md`
 * § Identifier boundary lets client code carry a server-issued identifier and
 * never create one.
 */

/** Amount and currency being typed. `currency` is unset until a tenant resolves it. */
export interface Draft {
  minorUnits: bigint;
  currency: string | undefined;
}

/** A transfer the reference flow is presenting. */
export interface ReferenceTransfer {
  /**
   * Opaque, bank-issued transfer reference.
   *
   * A fixed literal, never generated. Client code "may only represent, carry,
   * compare where appropriate, and return" server-issued identifiers.
   */
  readonly reference: string;
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly status: TransferStatusName;
  /** Hours left in the bank's claim window. The bank sets and enforces it. */
  readonly claimWindowHours: number;
}

/** The one synthetic reference this build ever shows. */
const SYNTHETIC_REFERENCE = "tr_8Qa4Zx2Ld7Nk1Bv9Rc6Tm";

/** Claim window shown throughout. A bank value, not a Nura one. */
const CLAIM_WINDOW_HOURS = 46;

/** Amount used only when `/status` is opened with no transfer in flight. */
const FALLBACK_AMOUNT_MINOR = 24500n;

const draft: Draft = { minorUnits: 0n, currency: undefined };

let active: ReferenceTransfer | undefined;

/** The amount currently being typed. */
export function draftAmount(): bigint {
  return draft.minorUnits;
}

/** The draft currency, once a tenant has resolved one. */
export function draftCurrency(): string | undefined {
  return draft.currency;
}

/**
 * Record what the composer produced.
 *
 * Editing the amount invalidates any transfer already carried forward: a
 * status screen describing the previous amount would be exactly the
 * inconsistency this module exists to remove.
 */
export function setDraft(minorUnits: bigint, currency: string): void {
  draft.minorUnits = minorUnits;
  draft.currency = currency;
  active = undefined;
}

/** Discard the draft and anything derived from it. */
export function clearDraft(): void {
  draft.minorUnits = 0n;
  active = undefined;
}

/**
 * The draft currency for `tenant`, reset when the tenant no longer supports it.
 *
 * Switching tenant can invalidate a draft currency — a Nordic institution that
 * only transacts in SEK cannot inherit a EUR draft. Falling back to the
 * tenant's default is the only safe answer; carrying the old code over would
 * offer a currency the bank does not accept.
 */
export function resolveDraftCurrency(tenant: Tenant): string {
  const current = draft.currency;
  if (current !== undefined && tenant.currency.supportedCurrencies.includes(current)) {
    return current;
  }
  clearDraft();
  draft.currency = tenant.currency.defaultCurrency;
  return draft.currency;
}

/**
 * Carry the draft forward as the transfer the status screen describes.
 *
 * The one place the two states meet. Called when the flow advances past
 * review; a zero draft cannot advance, so it produces nothing.
 */
export function commitDraft(): ReferenceTransfer | undefined {
  const currency = draft.currency;
  if (draft.minorUnits <= 0n || currency === undefined) return undefined;

  active = {
    reference: SYNTHETIC_REFERENCE,
    amountMinor: draft.minorUnits,
    currency,
    status: "AUTHORIZED_PENDING_CLAIM",
    claimWindowHours: CLAIM_WINDOW_HOURS,
  };
  return active;
}

/** The transfer carried forward from the flow, if there is one. */
export function activeTransfer(): ReferenceTransfer | undefined {
  return active;
}

/** Hours in the claim window, for screens that show it before commit. */
export function claimWindowHours(): number {
  return CLAIM_WINDOW_HOURS;
}

/**
 * A clearly synthetic transfer, for `/status` opened cold.
 *
 * Its currency follows the tenant so the fixture is at least coherent with the
 * institution on screen. It is only ever a fallback — `readTransferStatus`
 * prefers a real in-flight transfer, and a test holds that ordering, because
 * a fixture that could shadow the flow would reintroduce the bug this module
 * was written to remove.
 */
export function syntheticFallback(tenant: Tenant): ReferenceTransfer {
  return {
    reference: SYNTHETIC_REFERENCE,
    amountMinor: FALLBACK_AMOUNT_MINOR,
    currency: tenant.currency.defaultCurrency,
    status: "AUTHORIZED_PENDING_CLAIM",
    claimWindowHours: CLAIM_WINDOW_HOURS,
  };
}

/**
 * Read the transfer the status screen should present.
 *
 * Deliberately asynchronous and deliberately not a network call. §12 makes the
 * bank authoritative for every transition and Nura's copy "a lossy projection";
 * whatever eventually fills this in will be an awaited read of that projection,
 * so the view is built around one from the start rather than being retrofitted
 * with loading behaviour later.
 */
export function readTransferStatus(tenant: Tenant): Promise<ReferenceTransfer> {
  const transfer = active ?? syntheticFallback(tenant);
  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve(transfer);
    }, 420);
  });
}

/** Reset every piece of flow state. Test support only. */
export function resetReferenceFlow(): void {
  draft.minorUnits = 0n;
  draft.currency = undefined;
  active = undefined;
}
